﻿import React, { useState, useEffect } from 'react';
import { FaPlus, FaSearch, FaCalendar, FaUser, FaClock, FaExclamationTriangle, FaListAlt } from 'react-icons/fa';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { apiCall, ENDPOINTS } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';

interface WaitingListEntry {
  id: string;
  patient: string;
  patient_name: string;
  patient_mrn: string;
  provider: string;
  provider_name: string;
  appointment_type: string;
  appointment_type_name: string;
  duration_minutes: number;
  estimated_fee: number;
  priority: string;
  priority_display: string;
  status: string;
  status_display: string;
  preferred_date_from: string | null;
  preferred_date_to: string | null;
  preferred_time_of_day: string;
  days_waiting: number;
  reason: string;
  referral_required: boolean;
  referral_received: boolean;
  contact_phone: string;
  added_at: string;
}

interface ProviderSession {
  id: string;
  start_time: string;
  end_time: string;
  room: string | null;
  session_type: string | null;
  duration_minutes: number;
}

interface Provider {
  id: string;
  full_name: string;
}

const WaitingListCard: React.FC<{ entry: WaitingListEntry }> = ({ entry }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({ 
    id: entry.id,
    data: entry
  });

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    opacity: isDragging ? 0.5 : 1,
  } : undefined;

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'emergency': return 'bg-red-100 border-red-500';
      case 'urgent': return 'bg-orange-100 border-orange-500';
      case 'semi_urgent': return 'bg-yellow-100 border-yellow-500';
      default: return 'bg-blue-50 border-blue-300';
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`p-4 mb-3 border-l-4 rounded-lg shadow-sm cursor-move transition-all ${getPriorityColor(entry.priority)}`}
    >
      <div className="flex justify-between items-start mb-2">
        <div>
          <h4 className="font-semibold text-base-content">{entry.patient_name}</h4>
          <p className="text-sm text-base-content opacity-60">MRN: {entry.patient_mrn}</p>
        </div>
        {entry.priority !== 'routine' && (
          <span className="px-2 py-1 text-xs font-semibold rounded bg-error text-error-content">
            {entry.priority_display}
          </span>
        )}
      </div>
      
      <div className="space-y-1 text-sm">
        <div className="flex items-center text-base-content opacity-70">
          <FaUser className="mr-2" />
          <span>{entry.provider_name}</span>
        </div>
        <div className="flex items-center text-base-content opacity-70">
          <FaClock className="mr-2" />
          <span>{entry.appointment_type_name} ({entry.duration_minutes} min)</span>
        </div>
        {entry.referral_required && (
          <div className="flex items-center text-warning">
            <FaExclamationTriangle className="mr-2" />
            <span>Referral {entry.referral_received ? 'Received' : 'Required'}</span>
          </div>
        )}
        <div className="text-xs text-base-content opacity-50 mt-2">
          Waiting: {entry.days_waiting} days
        </div>
      </div>
    </div>
  );
};

const SessionSlot: React.FC<{
  session: ProviderSession;
  onDrop: (entry: WaitingListEntry, session: ProviderSession) => void;
}> = ({ session, onDrop }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: session.id,
    data: session
  });

  const startTime = new Date(session.start_time);
  const endTime = new Date(session.end_time);

  return (
    <div
      ref={setNodeRef}
      className={`p-3 mb-2 border-2 rounded-lg transition-all ${
        isOver
          ? 'border-success bg-success/10'
          : 'border-base-300 bg-base-100'
      }`}
    >
      <div className="flex justify-between items-center">
        <div>
          <p className="font-medium text-base-content">
            {startTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} -{' '}
            {endTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
          </p>
          <p className="text-sm text-base-content opacity-60">
            {session.room || 'No room assigned'} • {session.duration_minutes} min
          </p>
        </div>
        {isOver && (
          <span className="text-success text-sm font-semibold">Drop here</span>
        )}
      </div>
    </div>
  );
};

const WaitingList: React.FC = () => {
  const { token } = useAuth();
  const [waitingListEntries, setWaitingListEntries] = useState<WaitingListEntry[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [availableSessions, setAvailableSessions] = useState<ProviderSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [activeEntry, setActiveEntry] = useState<WaitingListEntry | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  useEffect(() => {
    loadProviders();
  }, []);

  useEffect(() => {
    if (token) {
      loadWaitingList();
    }
  }, [token, selectedProvider, priorityFilter, searchTerm]);

  useEffect(() => {
    if (selectedProvider && selectedDate) {
      loadAvailableSessions();
    }
  }, [selectedProvider, selectedDate]);

  const loadProviders = async () => {
    if (!token) return;
    try {
      const response = await apiCall('/providers/providers/', { method: 'GET' }, token);
      const data = await response.json();
      const providersList = data.results || data;
      setProviders(Array.isArray(providersList) ? providersList : []);
      if (providersList.length > 0) {
        setSelectedProvider(providersList[0].id);
      }
    } catch (error) {
      console.error('Error loading providers:', error);
    }
  };

  const loadWaitingList = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('status', 'active');
      if (selectedProvider) params.append('provider', selectedProvider);
      if (priorityFilter) params.append('priority', priorityFilter);
      if (searchTerm) params.append('search', searchTerm);

      const response = await apiCall(`/scheduling/waiting-list/?${params}`, { method: 'GET' }, token);
      const data = await response.json();
      setWaitingListEntries(data.results || data);
    } catch (error) {
      console.error('Error loading waiting list:', error);
      setWaitingListEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableSessions = async () => {
    if (!token || !selectedProvider || !selectedDate) return;
    try {
      const params = new URLSearchParams();
      params.append('provider', selectedProvider);
      params.append('date', selectedDate);

      const response = await apiCall(
        `/scheduling/waiting-list/available_slots/?${params}`,
        { method: 'GET' },
        token
      );
      const data = await response.json();
      setAvailableSessions(data.sessions || []);
    } catch (error) {
      console.error('Error loading available sessions:', error);
      setAvailableSessions([]);
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    const entry = waitingListEntries.find(e => e.id === event.active.id);
    if (entry) {
      setActiveEntry(entry);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveEntry(null);
    
    const { active, over } = event;
    
    if (!over || !token) return;
    
    const entry = waitingListEntries.find(e => e.id === active.id);
    const session = availableSessions.find(s => s.id === over.id);
    
    if (!entry || !session) return;
    
    // Validate duration
    if (entry.duration_minutes > session.duration_minutes) {
      alert(`Cannot fit ${entry.duration_minutes} min appointment in ${session.duration_minutes} min slot`);
      return;
    }

    await handleSchedule(entry, session);
  };

  const handleSchedule = async (entry: WaitingListEntry, session: ProviderSession) => {
    if (!token) return;

    const confirmed = window.confirm(
      `Schedule ${entry.patient_name} for ${entry.appointment_type_name} at ${new Date(
        session.start_time
      ).toLocaleTimeString()}?`
    );

    if (!confirmed) return;

    try {
      const response = await apiCall(
        `/scheduling/waiting-list/${entry.id}/schedule/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            provider_session_id: session.id,
            start_time: session.start_time,
            end_time: session.end_time
          })
        },
        token
      );

      if (response.ok) {
        alert('Appointment scheduled successfully!');
        loadWaitingList();
        loadAvailableSessions();
      } else {
        const error = await response.json();
        alert(`Error: ${error.error || 'Failed to schedule appointment'}`);
      }
    } catch (error) {
      console.error('Error scheduling appointment:', error);
      alert('Failed to schedule appointment');
    }
  };

  const filteredEntries = waitingListEntries;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-base-content flex items-center gap-3">
            <FaListAlt className="text-primary" />
            Waiting List
          </h1>
        </div>
        
        {/* Filters */}
        <div className="bg-base-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-6 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium whitespace-nowrap">Provider</span>
              <select
                className="select select-bordered"
                value={selectedProvider}
                onChange={(e) => setSelectedProvider(e.target.value)}
              >
                {providers.map((provider) => (
                  <option key={provider.id} value={provider.id}>
                    {provider.full_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm font-medium whitespace-nowrap">Priority</span>
              <select
                className="select select-bordered"
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
              >
                <option value="">All Priorities</option>
                <option value="emergency">Emergency</option>
                <option value="urgent">Urgent</option>
                <option value="semi_urgent">Semi-Urgent</option>
                <option value="routine">Routine</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm font-medium whitespace-nowrap">Search</span>
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content opacity-40" />
                <input
                  type="text"
                  className="input input-bordered pl-10"
                  placeholder="Name or MRN"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm font-medium whitespace-nowrap">Date</span>
              <input
                type="date"
                className="input input-bordered"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Main Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Panel: Waiting List */}
          <div className="bg-base-200 rounded-lg p-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-base-content">
                Waiting Patients ({filteredEntries.length})
              </h2>
              <button className="btn btn-primary btn-sm">
                <FaPlus className="mr-2" /> Add to List
              </button>
            </div>

            <div className="overflow-y-auto" style={{ maxHeight: '600px' }}>
              {loading ? (
                <div className="text-center py-10">
                  <span className="loading loading-spinner loading-lg"></span>
                </div>
              ) : filteredEntries.length === 0 ? (
                <div className="text-center py-10 text-base-content opacity-60">
                  No patients on waiting list
                </div>
              ) : (
                filteredEntries.map((entry) => (
                  <WaitingListCard key={entry.id} entry={entry} />
                ))
              )}
            </div>
          </div>

          {/* Right Panel: Available Slots */}
          <div className="bg-base-200 rounded-lg p-4">
            <h2 className="text-xl font-bold text-base-content mb-4">
              Available Slots - {new Date(selectedDate).toLocaleDateString()}
            </h2>

            <div className="overflow-y-auto" style={{ maxHeight: '600px' }}>
              {availableSessions.length === 0 ? (
                <div className="text-center py-10 text-base-content opacity-60">
                  <FaCalendar className="mx-auto text-4xl mb-2" />
                  <p>No available sessions for this date</p>
                </div>
              ) : (
                filteredEntries.map((entry) => (
                  <div key={entry.id} className="mb-6">
                    <h3 className="font-semibold text-base-content mb-2">
                      Available Slots ({entry.duration_minutes} min needed)
                    </h3>
                    {availableSessions
                      .filter(session => session.duration_minutes >= entry.duration_minutes)
                      .map((session) => (
                        <SessionSlot
                          key={session.id}
                          session={session}
                          onDrop={handleSchedule}
                        />
                      ))}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
      
      <DragOverlay>
        {activeEntry ? <WaitingListCard entry={activeEntry} /> : null}
      </DragOverlay>
    </DndContext>
  );
};

export default WaitingList;
